// Third Party
import * as React from 'react';
import ReactDOM from 'react-dom';
import { Transition, TransitionChild } from '@headlessui/react';
import cx from 'classnames';

// Utils
import { useMounted } from '@hooks';
import { isSSR } from '@utils/predicates';
import { ListingReview, useListingReviewsListAllForListingQuery } from '@gql';
import { capitalizeAllWords } from '@utils/string-helpers';
import { useReviews } from './reviews-context';

// UI
import { ButtonIcon } from '@components/button-icon';
import { Icon, IconNames } from '@components/icon';
import { Typography } from '@components/typography';
import { ReviewStars } from './review-stars';
import { Pagination } from '@components/pagination';
import { Spinner, SpinnerSize } from '@components/spinner';
import { ExpandableText } from '@components/expandable-text';

type Props = {
  listingId: string;
  reviewsAverageRating: number;
  reviewsCount: number;
};

export const ReviewsModal: React.FC<Props> = ({
  listingId,
  reviewsAverageRating,
  reviewsCount,
}) => {
  const mounted = useMounted();
  const { deactivateReviewsModal, reviewsModalActive, page, setPage } =
    useReviews();
  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      deactivateReviewsModal();
    }
  };

  const { data: listingReviewsData, loading: reviewsLoading } =
    useListingReviewsListAllForListingQuery({
      variables: {
        id: listingId,
        page: page,
        pageSize: 20,
      },
    });

  const totalPages = Math.ceil(reviewsCount / 20);

  const [listingReviews, setListingReviews] = React.useState<
    ListingReview[] | null
  >(null);

  React.useEffect(() => {
    if (
      listingReviewsData?.listingReviewsListAllForListing?.__typename ===
        'ListingReviewCollection' &&
      listingReviewsData?.listingReviewsListAllForListing.entries.length > 0
    ) {
      setListingReviews(
        listingReviewsData?.listingReviewsListAllForListing.entries
      );
    }
  }, [listingReviewsData]);

  React.useEffect(() => {
    if (reviewsModalActive) {
      document.body.classList.add('modalActive');
      document.addEventListener('keyup', handleEscapeKeyPress);
    } else {
      document.body.classList.remove('modalActive');
      document.removeEventListener('keyup', handleEscapeKeyPress);
    }

    // Cleanup function
    return () => {
      document.body.classList.remove('modalActive');
      document.removeEventListener('keyup', handleEscapeKeyPress);
    };
  }, [reviewsModalActive]);

  if (!mounted) return null;

  if (!isSSR) {
    return ReactDOM.createPortal(
      <Transition show={reviewsModalActive} as="div">
        <div
          className="fixed inset-0 h-screen overflow-hidden sm:px-8 z-10"
          style={{ zIndex: 10000 }}
        >
          <div className="flex items-end justify-center sm:px-4 text-center sm:block sm:p-0 h-screen sm:h-5/6 sm:max-h-[1082px]">
            <TransitionChild
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 transition-opacity">
                <div className="absolute inset-0 bg-gray-main opacity-75"></div>
              </div>
            </TransitionChild>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            &#8203;
            <TransitionChild
              as="div"
              className="fixed top-24 bottom-0 sm:relative sm:top-auto sm:bottom-auto inline-block align-bottom max-w-6xl text-left bg-white sm:h-5/6 overflow-hidden rounded-t-lg sm:rounded-lg shadow-xl transform transition-all sm:align-middle w-full lg:w-3/4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="flex flex-col h-full text-primary-main">
                <div className="flex flex-col w-full items-start">
                  <div className="flex flex-row items-center space-x-1  py-4 sm:py-6 text-center w-full justify-center border-b border-gray-light">
                    <ButtonIcon
                      variant="text"
                      color="gray"
                      size="sm"
                      isCircle={true}
                      icon={IconNames.X}
                      ariaDescribedby="close reviews modal"
                      className="absolute left-2 top-4 sm:top-6"
                      onClick={deactivateReviewsModal}
                    />
                    <Icon
                      name={IconNames.STAR}
                      solid={true}
                      className="-mt-0.5 text-sm md:text-lg"
                    />
                    <Typography
                      component="h4"
                      variant="title5"
                      variantM="title3"
                    >
                      {Number.isInteger(reviewsAverageRating)
                        ? reviewsAverageRating.toFixed(1)
                        : reviewsAverageRating.toFixed(2)}
                    </Typography>
                    <div className="px-1">
                      <Typography
                        component="h4"
                        variant="title5"
                        variantM="title3"
                      >
                        &bull;
                      </Typography>
                    </div>
                    <Typography
                      component="h4"
                      variant="title5"
                      variantM="title3"
                    >
                      {reviewsCount > 1
                        ? `${reviewsCount} reviews`
                        : '1 review'}
                    </Typography>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                  {reviewsLoading ? (
                    <div className="flex justify-center items-center pt-4">
                      <Spinner size={SpinnerSize.SIZE_LARGE} />
                    </div>
                  ) : (
                    <div className="space-y-6 font-semibold font-xs">
                      {listingReviews?.map((review, index) => (
                        <div
                          key={review.id}
                          className={cx('pb-6', {
                            'border-b border-gray-light':
                              index !== listingReviews.length - 1,
                          })}
                        >
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="flex items-center">
                              <ReviewStars rating={review.rating || 5} />
                            </div>
                            {review.date && (
                              <>
                                <div className="px-1">&bull;</div>
                                <Typography
                                  variant="subtitle2"
                                  component="span"
                                >
                                  {new Date(review.date).toLocaleDateString()}
                                </Typography>
                              </>
                            )}
                          </div>

                          <div>
                            <ExpandableText
                              text={review.body || ''}
                              maxLines={3}
                              maxLength={350}
                              className="text-primary"
                              typographyVariant="body2"
                              testId={`review-${review.id}`}
                              buttonColor="primary"
                              buttonVariant="text"
                              buttonClassName="-mt-2 underline"
                            />
                          </div>

                          {review.authorName && (
                            <Typography
                              component="p"
                              variant="body2"
                              className="mt-2"
                            >
                              - {capitalizeAllWords(review.authorName)}
                            </Typography>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-light px-2 py-4 sm:px-4 text-center w-full h-16">
                  {totalPages > page && (
                    <Pagination
                      onPageChange={(selectedPage) => {
                        setPage(selectedPage);
                      }}
                      currentPage={page}
                      totalPages={totalPages}
                    />
                  )}
                </div>
              </div>
            </TransitionChild>
          </div>
        </div>
      </Transition>,
      document.getElementById('modal-root') as Element
    );
  } else {
    return null;
  }
};
